import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TablePriceDisplay } from '@/components/ui-v2/inputs/table/table-price-display';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { formatCurrency } from '@/lib/utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { storeAtom } from '@/states/store.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

const useColumnDefPotentialSalesVolume =
  (): ExtendedColumnDef<DealItemWithVersions> => {
    const { t } = useTranslation();
    const store = useAtomValue(storeAtom);
    const { setColumnSort } = useSetColumnSort();
    const isOfferLocked = useIsOfferLocked();

    const getValue = (row: Row<DealItemWithVersions>) => {
      const value = row.original.activeVersion.potentialSalesVolume;
      if (typeof value === 'undefined' || value === null) return null;
      return value;
    };

    const getParentValue = (row: Row<DealItemWithVersions>) => {
      const subRowsValues: number[] = [];

      row.subRows.forEach((subRow) => {
        const value = subRow.original.activeVersion.potentialSalesVolume;
        if (typeof value !== 'undefined' && value !== null) {
          subRowsValues.push(value);
        }
      });

      if (subRowsValues.length === 0) {
        return null;
      }

      const sum = subRowsValues.reduce((acc, val) => acc + val, 0);
      return sum;
    };

    const accessorKey: keyof DealItem = 'potentialSalesVolume';
    return useMemo(
      () => ({
        accessorKey: accessorKey,
        name: 'PotentialSalesVolume',
        accessorFn: (row) => row.activeVersion?.potentialSalesVolume ?? 0,
        aggregationFn: 'mean',
        sortingFn: 'basic',
        header: () => (
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <InfoIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('kpi.potential_sales_volume_tooltip')}</p>
              </TooltipContent>
            </Tooltip>{' '}
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.potential_sales_volume')}
              <SortIcon pointer="potentialSalesVolume" />
            </div>
          </div>
        ),
        cell: ({ row }) => {
          const isParent = row.subRows.length > 1;
          const displayValue = isParent ? getParentValue(row) : getValue(row);

          return (
            <TableCellWithRecentlyModified
              productId={row.original.id}
              field="potentialSalesVolume"
              isParent={isParent}
            >
              {(isRecentlyModified) => (
                <div className="h-full w-full">
                  <TablePriceDisplay
                    value={
                      typeof displayValue === 'number'
                        ? formatCurrency(displayValue, store)
                        : null
                    }
                    isEmpty={displayValue === null}
                    isRecentlyModified={
                      isRecentlyModified && displayValue !== null
                    }
                    isParent={isParent}
                    isEditable={!isOfferLocked}
                  />
                </div>
              )}
            </TableCellWithRecentlyModified>
          );
        },
        size: 160,
      }),
      [t, store, setColumnSort, isOfferLocked]
    );
  };

export default useColumnDefPotentialSalesVolume;
